import type { CreativeSummary, CreativesResponse, ObservedMetrics } from '../creatives/contract'
import type { ConfidenceBand, ConfidenceThresholds } from '../creatives/confidence'
import { reliabilityOf } from '../creatives/confidence'
import type { Ratio } from '../creatives/metrics'
import { aggregateUnder, derive } from '../creatives/metrics'
import { launched, UNASSIGNED } from '../creatives/partition'

/**
 * The comparison's pooling: the buckets are a regrouping of the rows the creatives screen
 * draws, never a second data set, so a row and the list it opens are one calculation read
 * twice (`docs/working/design-refactoring-03.md`, step 7).
 *
 * Hypothesis and technique are single-valued per hash, so pooling the unscoped response on
 * the client partitions it exactly on either attribute axis. Audience is a scope, not an
 * attribute: one asset can run in several ad sets against several audiences, and after
 * merging by hash the client cannot tell which spend belonged to which audience - so the
 * audience buckets are pooled from one scoped response per audience (`poolAudience`), never
 * client-side from the merged rows.
 */

export type Axis = 'hypothesis' | 'technique' | 'audience'

export interface BucketRow {
  axis: Axis
  /**
   * The dimension id, always a string. A creative whose attribute is null on the axis
   * being pooled pools under `UNASSIGNED`, on either attribute axis: the conversion from
   * a null attribute to the sentinel happens in `pool` and nowhere else, so the screen,
   * the drill callback and the row keys see one shape.
   */
  id: string
  /** The members, keyed by `id`, the asset hash. Never by name. */
  creatives: CreativeSummary[]
  /** Sums over the members under the contract's null and incomplete rules. */
  metrics: ObservedMetrics
  /** The two ratios the screen shows, each null on a zero or null denominator. */
  roas: Ratio
  cpa: Ratio
  /** The reliability band of the pooled conversions, classified with the screen's thresholds. */
  band: ConfidenceBand
  /**
   * Any of the three outcome measures - `spend`, `conversions`, `conversionValue` - is
   * incomplete on the bucket: the pooled sample is not fully observed. Parking and the
   * proposal read this, never `conversions` alone, because the contract makes
   * `outcomePartial` out of all three and a bucket with reported conversions but
   * incomplete spend ranks on a ratio whose cost side is partly unseen.
   */
  outcomeIncomplete: boolean
}

function bucketOf(
  axis: Axis,
  id: string,
  members: CreativeSummary[],
  thresholds: ConfidenceThresholds,
  scopePartial: boolean,
): BucketRow {
  const metrics = aggregateUnder(members.map((creative) => creative.metrics), scopePartial)
  const derived = derive(metrics)
  return {
    axis,
    id,
    creatives: members,
    metrics,
    roas: derived.roas,
    cpa: derived.cpa,
    band: reliabilityOf(metrics.conversions ?? 0, thresholds),
    outcomeIncomplete: derived.outcomePartial,
  }
}

/**
 * Pool the creatives of one response into buckets on an attribute axis.
 *
 * The band classifies through `reliabilityOf` with the thresholds the screen passes, so a
 * custom provisional floor moves the ranking, the ticks, the awaiting line and the
 * footnote clause together. `scopePartial` (D8) marks every bucket's measures, so every
 * bucket lands in `partial`: the scope holds delivery no bucket can be credited with.
 */
export function pool(
  creatives: CreativeSummary[],
  axis: 'hypothesis' | 'technique',
  thresholds: ConfidenceThresholds,
  scopePartial: boolean,
): BucketRow[] {
  const groups = new Map<string, CreativeSummary[]>()
  for (const creative of launched(creatives)) {
    const attribute = axis === 'hypothesis' ? creative.hypothesisId : creative.techniqueId
    const id = attribute ?? UNASSIGNED
    const group = groups.get(id)
    if (group) group.push(creative)
    else groups.set(id, [creative])
  }
  return [...groups].map(([id, members]) => bucketOf(axis, id, members, thresholds, scopePartial))
}

/**
 * One bucket per audience, each from its own scoped response.
 *
 * The bucket set is the unscoped response's `dimensions.audiences` - the audiences the
 * account-scoped rows use, so every bucket has something behind it and none renders empty.
 * The screen issues one scoped request per audience and fails the axis when any of them
 * fails, so a scoped response missing here would mean the caller pooled before the fetches
 * settled, and a bucket silently dropped would break the partition the screen exists to
 * keep: the sums would no longer reconcile with the totals row.
 *
 * `scopePartial` is read from the unscoped response by the caller: the scope counts are
 * never narrowed by audience, so every audience bucket is partial when the scope is.
 */
export function poolAudience(
  response: CreativesResponse,
  scoped: CreativesResponse[],
  thresholds: ConfidenceThresholds,
  scopePartial: boolean,
): BucketRow[] {
  const byId = new Map<string, CreativesResponse>()
  for (const one of scoped) {
    const id = one.scope.audienceId
    if (id !== null) byId.set(id, one)
  }
  return response.dimensions.audiences.map(({ id }) => {
    const one = byId.get(id)
    if (!one) throw new Error(`poolAudience: no scoped response for audience ${id}`)
    return bucketOf('audience', id, launched(one.creatives), thresholds, scopePartial)
  })
}

function bySpendDesc(left: BucketRow, right: BucketRow): number {
  const gap = (right.metrics.spend ?? 0) - (left.metrics.spend ?? 0)
  return gap !== 0 ? gap : left.id.localeCompare(right.id)
}

/**
 * Every bucket in one list: ROAS descending, ties by spend descending, `null` ROAS last.
 *
 * A bucket with a thin sample or a partly reported outcome keeps its place in the order;
 * the row itself carries the warning.
 */
export function orderBuckets(rows: BucketRow[]): BucketRow[] {
  return [...rows].sort((left, right) => {
    const leftRoas = left.roas.value
    const rightRoas = right.roas.value
    if (leftRoas === null && rightRoas === null) return bySpendDesc(left, right)
    if (leftRoas === null) return 1
    if (rightRoas === null) return -1
    const gap = rightRoas - leftRoas
    return gap !== 0 ? gap : bySpendDesc(left, right)
  })
}

/**
 * The totals section's figures: `aggregateObserved` over the whole response.
 *
 * The buckets partition the response on every axis, so a bucket set's sums and this
 * totals row describe the same numbers twice; on the audience axis the measures partition
 * over ads while creative rows can sit in two buckets, and the totals row is what makes
 * that visible instead of hiding it.
 */
export function bucketTotals(creatives: CreativeSummary[], scopePartial: boolean): {
  metrics: ObservedMetrics
  roas: Ratio
  cpa: Ratio
} {
  const metrics = aggregateUnder(launched(creatives).map((creative) => creative.metrics), scopePartial)
  const derived = derive(metrics)
  return { metrics, roas: derived.roas, cpa: derived.cpa }
}

/** The distinct version letters a bucket's creatives carry, sorted (GRO-159). */
export function versionsOf(bucket: BucketRow): string[] {
  const letters = new Set<string>()
  for (const creative of bucket.creatives) {
    if (creative.versionLabel !== null) letters.add(creative.versionLabel)
  }
  return [...letters].sort()
}
